(() => {
  const copyByTitle = {
    'Nur Pfotenpads': 'Sicherer Halt bei jedem Schritt',
    'Pfotenpflege-Set': 'Mehr Halt & saubere Pfoten',
    'Premium-Set': 'Pflege, Schutz & sicherer Halt',
  };

  const giftCopyByTitle = {
    'Pfotenpflege-Set': ['inkl. 3 × 100 Pfoten-Feuchttücher'],
    'Premium-Set': [
      'inkl. 3 × 100 Pfoten-Feuchttücher',
      'inkl. 2 × 30 g Pfotenbalsam',
    ],
  };

  const observedRoots = new WeakSet();
  const observedButtons = new WeakSet();
  const observedBoxes = new WeakSet();
  const observedSubscriptions = new WeakSet();
  const scheduledRoots = new WeakMap();

  const cleanText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  function setText(element, value) {
    if (!element || cleanText(element.textContent) === value) return;
    element.textContent = value;
  }

  function isVisible(element) {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  function makeBoxKeyboardAccessible(box) {
    if (observedBoxes.has(box)) return;
    box.addEventListener('keydown', (event) => {
      if (event.target !== box || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      box.click();
    });
    observedBoxes.add(box);
  }

  function enhanceCard(box, title) {
    box.querySelectorAll('.pm-bundle-choice-indicator, .pm-bundle-field-label').forEach((element) => element.remove());
    box.classList.toggle('pm-bundle-card--preferred', title === 'Pfotenpflege-Set');
    box.classList.toggle('pm-bundle-card--premium', title === 'Premium-Set');
    box.dataset.pmBundleTitle = title;
    box.setAttribute('tabindex', '0');
    box.setAttribute('role', 'button');
    makeBoxKeyboardAccessible(box);

    const badge = box.querySelector('.st_highlighted-badge');
    if (!badge) return;

    badge.classList.remove(
      'pm-bundle-inline-badge',
      'pm-bundle-inline-badge--premium',
      'pm-bundle-inline-badge--recommendation'
    );
    if (title === 'Pfotenpflege-Set') setText(badge, 'Meistgewählt');
    if (title === 'Premium-Set') setText(badge, 'Komplett ausgestattet');
    if (badge.parentElement !== box) box.prepend(badge);
  }

  function enhanceGiftCopy(box, title) {
    const giftCopy = giftCopyByTitle[title];
    if (!giftCopy) return;

    box.querySelectorAll('.st_gift-product-title').forEach((giftTitle, index) => {
      if (giftCopy[index]) setText(giftTitle, giftCopy[index]);
    });
  }

  function enhanceSelectors(box) {
    setText(box.querySelector('.st_selectors-label'), 'Größe & Menge');
    const indexLabel = box.querySelector('.st_select-box-index');
    if (indexLabel) indexLabel.setAttribute('aria-hidden', 'true');

    box.querySelectorAll('select').forEach((selector, index) => {
      const fieldName = index === 0 ? 'Größe' : 'Menge';
      if (selector.getAttribute('aria-label') !== fieldName) selector.setAttribute('aria-label', fieldName);
    });
  }

  function enhanceSubscription(root) {
    const subscription = root.querySelector('.st_subscription-checkbox');
    if (!subscription) return;

    const isChecked = subscription.classList.contains('st_subscription-checked');
    subscription.setAttribute('role', 'checkbox');
    subscription.setAttribute('tabindex', '0');
    subscription.setAttribute('aria-checked', isChecked ? 'true' : 'false');
    setText(root.querySelector('.st_subscription-title'), '10 % mit Pfotenpad-Abo sparen');
    setText(
      root.querySelector('.st_subscription-subtitle'),
      'Neue Pfotenpads automatisch erhalten. Jederzeit kündbar, keine Mindestlaufzeit.'
    );

    if (observedSubscriptions.has(subscription)) return;
    subscription.addEventListener('keydown', (event) => {
      if (!['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      subscription.click();
    });
    observedSubscriptions.add(subscription);
  }

  function updateSelectionState(root, boxes) {
    const activeBox = boxes.find((box) => box.classList.contains('active'));
    const activeTitle = cleanText(activeBox?.querySelector('.st_input-title')?.textContent);

    boxes.forEach((box) => {
      const selected = box === activeBox;
      box.dataset.pmSelected = selected ? 'true' : 'false';
      box.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const selectors = activeBox ? Array.from(activeBox.querySelectorAll('select:not([disabled])')) : [];
    const unavailableMessage = activeBox?.querySelector('.st_unavailable-message');
    const selectionReady = Boolean(
      activeBox &&
      selectors.every((selector) => selector.value) &&
      !(unavailableMessage && isVisible(unavailableMessage))
    );
    const previousReady = root.dataset.pmBundleSelectionReady;
    const previousTitle = root.dataset.pmBundleSelectedTitle;

    root.dataset.pmBundleSelectionReady = String(selectionReady);
    root.dataset.pmBundleSelectedTitle = activeTitle;

    if (previousReady !== String(selectionReady) || previousTitle !== activeTitle) {
      root.dispatchEvent(
        new CustomEvent('pm:bundle-selection-change', {
          bubbles: true,
          detail: { ready: selectionReady, title: activeTitle },
        })
      );
    }
  }

  function updateSubmitButton(root) {
    const productInfo = root.closest('product-info');
    const button = productInfo?.querySelector('.product-form__submit');
    const label = button?.querySelector('span');
    if (!button || !label) return;

    observeButton(root, button);
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const subscriptionActive = Boolean(
      root.querySelector('.st_subscription-checkbox.st_subscription-checked')
    );
    setText(label, subscriptionActive ? 'Mit Spar-Abo hinzufügen' : 'Einmalig hinzufügen');
  }

  function observeButton(root, button) {
    if (observedButtons.has(button)) return;

    new MutationObserver(() => scheduleEnhance(root)).observe(button, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      characterData: true,
      subtree: true,
    });
    observedButtons.add(button);
  }

  function enhance(root) {
    const boxes = Array.from(root.querySelectorAll('.st_bundle-box'));
    if (!boxes.length) return;

    boxes.forEach((box) => {
      const title = cleanText(box.querySelector('.st_input-title')?.textContent);
      const microcopy = box.querySelector('.st_input-highlighted-tag');
      const copy = copyByTitle[title];

      enhanceCard(box, title);
      if (microcopy && copy) {
        microcopy.classList.add('pm-bundle-microcopy');
        if (microcopy.dataset.template !== copy) microcopy.dataset.template = copy;
        setText(microcopy, copy);
      }
      enhanceGiftCopy(box, title);
      enhanceSelectors(box);
    });

    updateSelectionState(root, boxes);
    enhanceSubscription(root);
    updateSubmitButton(root);
  }

  function scheduleEnhance(root) {
    if (scheduledRoots.has(root)) return;

    scheduledRoots.set(
      root,
      requestAnimationFrame(() => {
        scheduledRoots.delete(root);
        enhance(root);
      })
    );
  }

  function init(scope = document) {
    const roots = [];
    if (scope.matches?.('#sternify-bundle')) roots.push(scope);
    roots.push(...scope.querySelectorAll('#sternify-bundle'));

    roots.forEach((root) => {
      enhance(root);
      if (observedRoots.has(root)) return;

      new MutationObserver(() => scheduleEnhance(root)).observe(root, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'style'],
        characterData: true,
        childList: true,
        subtree: true,
      });

      root.addEventListener('click', () => scheduleEnhance(root));
      root.addEventListener('change', () => scheduleEnhance(root));
      observedRoots.add(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
